<template lang="pug">
v-container(fluid)
  v-card.mb-6
    v-card-title.headline
      | Reporte de Campañas de Marketing
    v-card-subtitle
      | Analizá el rendimiento de tus campañas de Paid Media

    v-card-text
      v-row
        v-col(cols="12" sm="6" md="3")
          v-card(outlined class="pa-3 text-center")
            div.subtitle-1.font-weight-medium Órdenes
            div.display-1.font-weight-bold.primary--text
              | {{ $store.state.marketing.reportSummary.totalOrders || 0 }}
        
        v-col(cols="12" sm="6" md="3")
          v-card(outlined class="pa-3 text-center")
            div.subtitle-1.font-weight-medium Reservas
            div.display-1.font-weight-bold.primary--text
              | {{ $store.state.marketing.reportSummary.totalReservations || 0 }}
        
        v-col(cols="12" sm="6" md="3")
          v-card(outlined class="pa-3 text-center")
            div.subtitle-1.font-weight-medium Facturación
            div.display-1.font-weight-bold.primary--text
              | ${{ formatPrice($store.state.marketing.reportSummary.totalRevenue || 0) }}
        
        v-col(cols="12" sm="6" md="3")
          v-card(outlined class="pa-3 text-center")
            div.subtitle-1.font-weight-medium Período
            div.subtitle-1.font-weight-bold
              | {{ formattedDateRange }}

  v-card.mb-6
    v-card-title
      | Filtros
      v-spacer
      v-btn(color="primary" @click="fetchReports")
        v-icon(left) mdi-filter
        | Aplicar filtros
    
    v-card-text
      v-row
        v-col(cols="12" sm="6" md="3")
          v-menu(
            v-model="dateMenu"
            :close-on-content-click="false"
            max-width="290"
          )
            template(v-slot:activator="{ on, attrs }")
              v-text-field(
                v-model="dateRangeText"
                label="Rango de fechas"
                readonly
                clearable
                outlined
                dense
                prepend-icon="mdi-calendar-range"
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDateRange"
              )
            v-date-picker(
              v-model="dateRangeArray"
              range
              no-title
              locale="es-AR"
              @change="dateMenu = false"
            )
        v-col(cols="12" sm="6" md="3")
          v-autocomplete(
            v-model="filters.utmSource"
            :items="sourceOptions"
            label="Fuente (UTM Source)"
            clearable
            chips
            outlined
            dense
          )
        
        v-col(cols="12" sm="6" md="3")
          v-autocomplete(
            v-model="filters.utmMedium"
            :items="mediumOptions"
            label="Medio (UTM Medium)"
            clearable
            chips
            outlined
            dense
          )
        
        v-col(cols="12" sm="6" md="3")
          v-autocomplete(
            v-model="filters.utmCampaign"
            :items="campaignOptions"
            label="Campaña (UTM Campaign)"
            clearable
            chips
            outlined
            dense
          )

  v-data-table(
    :headers="headers"
    :items="campaignReports"
    :items-per-page="10"
    :loading="loading"
    :footer-props="{ 'items-per-page-options': [10, 20, 50], showFirstLastPage: true }"
    class="elevation-1"
  )
    template(v-slot:item.date="{ item }")
      | {{ formatDate(item.date) }}
    
    template(v-slot:item.utmSource="{ item }")
      v-chip(small :color="getSourceColor(item.utmSource)" text-color="white")
        | {{ item.utmSource }}
    
    template(v-slot:item.revenue="{ item }")
      | ${{ formatPrice(item.revenue) }}
    
    template(v-slot:item.deviceTypes="{ item }")
      v-chip(
        v-for="(device, i) in item.deviceTypes" 
        :key="i" 
        small 
        class="mr-1"
        :color="getDeviceColor(device)"
      )
        | {{ device }}
    
    template(v-slot:no-data)
      v-alert(
        :value="true"
        color="info"
        outlined
      )
        | No hay datos disponibles para los filtros seleccionados
</template>

<script>
import { mapState } from 'vuex';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default {
  name: 'CampaignsReport',

  data() {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    return {
      dateMenu: false,
      dateRangeArray: [
        format(firstDayOfMonth, 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
      ],
      dateRange: {
        start: firstDayOfMonth,
        end: today
      },
      filters: {
        utmSource: null,
        utmMedium: null,
        utmCampaign: null
      },
      headers: [
        { text: 'Fecha', value: 'date' },
        { text: 'Fuente', value: 'utmSource' },
        { text: 'Medio', value: 'utmMedium' },
        { text: 'Campaña', value: 'utmCampaign' },
        { text: 'Órdenes', value: 'orders' },
        { text: 'Reservas', value: 'reservations' },
        { text: 'Facturación', value: 'revenue' },
        { text: 'Dispositivos', value: 'deviceTypes' }
      ],
      sourceColors: {
        google: 'blue',
        facebook: 'indigo',
        instagram: 'purple',
        tiktok: 'black',
        email: 'red',
        whatsapp: 'green'
      },
      deviceColors: {
        mobile: 'blue',
        desktop: 'indigo',
        tablet: 'purple'
      }
    };
  },

  computed: {
    ...mapState('marketing', ['campaignReports', 'loading', 'error']),
    
    formattedDateRange() {
      if (!this.dateRange.start || !this.dateRange.end) return '';
      
      return `${format(this.dateRange.start, 'dd/MM/yyyy')} - ${format(this.dateRange.end, 'dd/MM/yyyy')}`;
    },
    
    dateRangeText() {
      if (this.dateRangeArray.length !== 2) return '';
      
      // Usar la misma lógica de ExpensesView para evitar desfase de fecha
      const formatDateLocal = (dateString) => {
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dateString.split('-').map(Number);
          const localDate = new Date(year, month - 1, day); // month es 0-indexed
          return format(localDate, 'dd/MM/yyyy', { locale: es });
        }
        return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
      };
      
      return `${formatDateLocal(this.dateRangeArray[0])} ~ ${formatDateLocal(this.dateRangeArray[1])}`;
    },
    
    sourceOptions() {
      // Crear opciones únicas de fuentes a partir de los datos
      const sources = new Set();
      this.campaignReports.forEach(report => {
        if (report.utmSource) sources.add(report.utmSource);
      });
      return Array.from(sources);
    },
    
    mediumOptions() {
      // Crear opciones únicas de medios a partir de los datos
      const mediums = new Set();
      this.campaignReports.forEach(report => {
        if (report.utmMedium) mediums.add(report.utmMedium);
      });
      return Array.from(mediums);
    },
    
    campaignOptions() {
      // Crear opciones únicas de campañas a partir de los datos
      const campaigns = new Set();
      this.campaignReports.forEach(report => {
        if (report.utmCampaign) campaigns.add(report.utmCampaign);
      });
      return Array.from(campaigns);
    }
  },

  watch: {
    dateRangeArray(val) {
      if (val.length === 2) {
        // Usar la misma lógica de ExpensesView para crear fechas locales
        const createLocalDate = (dateString) => {
          if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dateString.split('-').map(Number);
            return new Date(year, month - 1, day); // month es 0-indexed
          }
          return new Date(dateString);
        };
        
        this.dateRange = {
          start: createLocalDate(val[0]),
          end: createLocalDate(val[1])
        };
        
        // Búsqueda automática al cambiar fechas (más fluido)
        this.$nextTick(() => {
          this.fetchReports();
        });
      }
    }
  },

  created() {
    this.fetchReports();
  },

  methods: {
    async fetchReports() {
      try {
        await this.$store.dispatch('marketing/fetchCampaignReports', {
          filters: {
            startDate: this.dateRange.start.toISOString(),
            endDate: this.dateRange.end.toISOString(),
            utmSource: this.filters.utmSource,
            utmMedium: this.filters.utmMedium,
            utmCampaign: this.filters.utmCampaign
          }
        });
      } catch (error) {
        console.error('Error al cargar reportes:', error);
      }
    },
    
    clearDateRange() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      
      this.dateRangeArray = [
        format(firstDayOfMonth, 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
      ];
      
      this.dateRange = {
        start: firstDayOfMonth,
        end: today
      };
    },
    
    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        // Formatear la fecha con el locale español (día/mes/año)
        return format(date, 'dd/MM/yyyy', { locale: es });
      } catch (e) {
        return dateString;
      }
    },
    
    formatPrice(value) {
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    
    getSourceColor(source) {
      return this.sourceColors[source.toLowerCase()] || 'grey';
    },
    
    getDeviceColor(device) {
      return this.deviceColors[device.toLowerCase()] || 'grey';
    }
  }
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

@media (max-width: 600px) {
  .headline {
    font-size: 1.25rem !important;
  }
  
  .display-1 {
    font-size: 1.5rem !important;
  }
}
</style>
